import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { CheckSquare, ClipboardList, BarChart3, Loader2, Check, X, Minus, Download, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = { done: 'bg-emerald-500', pending: 'bg-zinc-600', failed: 'bg-rose-500' };
const GANTT_COLORS = ['#2563EB', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6', '#ec4899'];

export function ChecklistTab({ projectId, documents }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { API.get(`/checklists/${projectId}`).then(r => setChecklists(r.data)).catch(() => {}); }, [projectId]);

  const generate = async () => {
    setGenerating(true);
    try { await API.post(`/generate/checklist/${projectId}`); toast.success('Checklist generat!');
      API.get(`/checklists/${projectId}`).then(r => setChecklists(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setGenerating(false); }
  };

  const toggleItem = async (clId, idx, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'pending' : 'done';
    try { await API.put(`/checklists/${clId}/item/${idx}`, { status: newStatus });
      API.get(`/checklists/${projectId}`).then(r => setChecklists(r.data));
    } catch {}
  };

  const latest = checklists[0];
  const items = latest?.items || [];
  const categories = [...new Set(items.map(i => i.category))];
  const doneCount = items.filter(i => i.status === 'done').length;
  const progress = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <CheckSquare className="w-4 h-4 text-emerald-400" />Checklist Pregatire Oferta
          </h3>
          {items.length > 0 && <p className="text-xs text-muted-foreground mt-1">{doneCount}/{items.length} completate ({progress}%)</p>}
        </div>
        <Button onClick={generate} disabled={generating || !documents.length} size="sm" data-testid="generate-checklist-btn">
          {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckSquare className="w-3.5 h-3.5 mr-1.5" />}
          {generating ? 'Se genereaza...' : 'Genereaza Checklist'}
        </Button>
      </div>

      {items.length > 0 && <Progress value={progress} className="h-2" />}

      {categories.map(cat => (
        <Card key={cat} className="bg-card border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground tracking-wider">{cat}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {items.filter(i => i.category === cat).map((item, idx) => {
              const globalIdx = items.indexOf(item);
              return (
                <div key={globalIdx} className="flex items-center gap-3 p-2 rounded hover:bg-white/5 transition-colors" data-testid={`checklist-item-${globalIdx}`}>
                  <button onClick={() => toggleItem(latest.id, globalIdx, item.status)}
                    className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${
                      item.status === 'done' ? 'bg-emerald-500 text-white' : 'border border-white/20 hover:border-primary'
                    }`}>
                    {item.status === 'done' && <Check className="w-3 h-3" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${item.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{item.requirement}</p>
                    {item.notes && <p className="text-[10px] text-muted-foreground">{item.notes}</p>}
                  </div>
                  {item.responsible && <span className="text-[10px] text-primary shrink-0">{item.responsible}</span>}
                  {item.deadline && <span className="text-[10px] text-amber-400 font-mono-data shrink-0">{item.deadline}</span>}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
      {!items.length && <p className="text-center text-sm text-muted-foreground py-6">Genereaza un checklist din documentele proiectului</p>}
    </div>
  );
}

export function ComplianceMatrixTab({ projectId, documents }) {
  const [matrices, setMatrices] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [localSections, setLocalSections] = useState([]);
  const [checkResult, setCheckResult] = useState(null);

  const reload = () => API.get(`/compliance-matrices/${projectId}`).then(r => setMatrices(r.data)).catch(() => {});
  useEffect(() => { API.get(`/compliance-matrices/${projectId}`).then(r => setMatrices(r.data)).catch(() => {}); }, [projectId]);

  const latest = matrices[0];
  useEffect(() => { setLocalSections(latest?.sections || []); setCheckResult(null); }, [latest]);

  const generate = async () => {
    setGenerating(true);
    try { await API.post(`/generate/compliance-matrix/${projectId}`); toast.success('Matrice generata!'); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setGenerating(false); }
  };

  const allItems = localSections.flatMap(s => s.items || []);
  const compliant = allItems.filter(i => i.compliant === true).length;
  const partial = allItems.filter(i => i.compliant === 'partial').length;
  const nonCompliant = allItems.filter(i => i.compliant === false).length;
  const todo = allItems.length - compliant - partial - nonCompliant;

  const saveItem = async (si, ii, patch) => {
    setLocalSections(prev => {
      const next = prev.map(s => ({ ...s, items: [...(s.items || [])] }));
      next[si].items[ii] = { ...next[si].items[ii], ...patch };
      return next;
    });
    setCheckResult(null);
    if (!latest?.id) return;
    try { await API.put(`/compliance-matrices/${latest.id}/item`, { section_index: si, item_index: ii, ...patch }); }
    catch { toast.error('Eroare la salvare'); }
  };

  const cycleStatus = (si, ii, current) => {
    const order = [true, 'partial', false, null];
    const idx = order.findIndex(v => v === current);
    saveItem(si, ii, { compliant: order[(idx + 1) % order.length] });
  };

  const runPreSubmissionCheck = () => {
    const blockers = [];
    localSections.forEach(s => (s.items || []).forEach(it => {
      if (it.mandatory && it.compliant !== true) blockers.push(it);
    }));
    setCheckResult({ blockers, total: allItems.length });
  };

  const downloadExcel = async () => {
    if (!latest?.id) return;
    try {
      const res = await API.get(`/export/compliance-matrix/${latest.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `matrice_conformitate_${latest.id.slice(0,8)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Eroare export'); }
  };

  const statusBadge = (c) => {
    if (c === true) return <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px] cursor-pointer gap-1"><Check className="w-3 h-3" />Conform</Badge>;
    if (c === 'partial') return <Badge className="bg-amber-500/20 text-amber-400 text-[10px] cursor-pointer">Partial</Badge>;
    if (c === false) return <Badge className="bg-rose-500/20 text-rose-400 text-[10px] cursor-pointer gap-1"><X className="w-3 h-3" />Neconf.</Badge>;
    return <Badge className="bg-zinc-500/20 text-zinc-400 text-[10px] cursor-pointer gap-1"><Minus className="w-3 h-3" />De verif.</Badge>;
  };

  const sections = localSections;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <ClipboardList className="w-4 h-4 text-primary" />Matrice de Conformitate
          </h3>
          {allItems.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {allItems.length} cerinte | <span className="text-emerald-400">{compliant} conform</span> | <span className="text-amber-400">{partial} partial</span> | <span className="text-rose-400">{nonCompliant} neconform</span> | {todo} de verificat
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {latest?.id && sections.length > 0 && (
            <Button onClick={runPreSubmissionCheck} size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" data-testid="pre-submission-check-btn">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Verificare Pre-Depunere
            </Button>
          )}
          {latest?.id && sections.length > 0 && (
            <Button onClick={downloadExcel} size="sm" variant="outline" className="border-white/10" data-testid="export-matrix-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" />Excel
            </Button>
          )}
          <Button onClick={generate} disabled={generating || !documents.length} size="sm" data-testid="generate-matrix-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5 mr-1.5" />}
            {generating ? 'Se genereaza...' : 'Genereaza Matrice'}
          </Button>
        </div>
      </div>

      {checkResult && (
        checkResult.blockers.length === 0 ? (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.08] p-4 flex items-start gap-3" data-testid="pre-submission-ok">
            <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-300" style={{ fontFamily: 'Outfit' }}>Gata de depunere ✓</p>
              <p className="text-xs text-muted-foreground mt-1">Toate cele {allItems.length} cerinte au fost verificate si toate cerintele OBLIGATORII sunt Conform. Oferta poate fi depusa.</p>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/[0.08] p-4" data-testid="pre-submission-blocked">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-rose-300" style={{ fontFamily: 'Outfit' }}>DEPUNERE BLOCATA — {checkResult.blockers.length} cerinte obligatorii neconforme</p>
                <p className="text-xs text-muted-foreground mt-1">Rezolva-le pe toate (status Conform) inainte de depunere, ca sa nu fii declarat NECONFORM:</p>
                <ul className="mt-2 space-y-1.5">
                  {checkResult.blockers.map((b, i) => (
                    <li key={i} className="text-xs flex items-start gap-2" data-testid={`blocker-${i}`}>
                      <span className="font-mono-data text-rose-400 shrink-0">{b.id}</span>
                      <span className="text-foreground/90">{b.requirement}</span>
                      {b.notes && <span className="text-amber-300/80">— {b.notes}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )
      )}

      {sections.map((section, si) => (
        <Card key={si} className="bg-card border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase text-muted-foreground tracking-wider">{section.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-[10px] text-muted-foreground uppercase py-2 pr-3 w-14">ID</th>
                    <th className="text-left text-[10px] text-muted-foreground uppercase py-2 pr-3 w-1/4">Cerinta (din caiet)</th>
                    <th className="text-center text-[10px] text-muted-foreground uppercase py-2 w-14">Oblig.</th>
                    <th className="text-center text-[10px] text-muted-foreground uppercase py-2 w-20">Status</th>
                    <th className="text-left text-[10px] text-muted-foreground uppercase py-2 pr-3">Raspuns Oferta SAGA</th>
                    <th className="text-left text-[10px] text-muted-foreground uppercase py-2">Gap / Actiune</th>
                  </tr>
                </thead>
                <tbody>
                  {(section.items || []).map((item, ii) => (
                    <tr key={ii} className="border-b border-white/5 hover:bg-white/5 align-top" data-testid={`matrix-item-${si}-${ii}`}>
                      <td className="py-2 pr-3 font-mono-data text-xs text-muted-foreground">{item.id}</td>
                      <td className="py-2 pr-3 text-xs">{item.requirement}</td>
                      <td className="py-2 text-center">{item.mandatory ? <span className="text-rose-400 text-xs font-bold">DA</span> : <span className="text-zinc-500 text-xs">NU</span>}</td>
                      <td className="py-2 text-center">
                        <button onClick={() => cycleStatus(si, ii, item.compliant)} title="Click pentru a schimba statusul" data-testid={`matrix-status-${si}-${ii}`}>
                          {statusBadge(item.compliant)}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        <Textarea defaultValue={item.offer_reference || ''} onBlur={e => { if (e.target.value !== (item.offer_reference || '')) saveItem(si, ii, { offer_reference: e.target.value }); }}
                          placeholder="Raspunsul ofertei SAGA..." className="min-h-[38px] text-xs bg-transparent border-white/5 hover:border-white/15 focus:border-primary/40 resize-y py-1" data-testid={`matrix-offer-${si}-${ii}`} />
                      </td>
                      <td className="py-2">
                        <Textarea defaultValue={item.notes || ''} onBlur={e => { if (e.target.value !== (item.notes || '')) saveItem(si, ii, { notes: e.target.value }); }}
                          placeholder="Gap / actiune..." className="min-h-[38px] text-xs bg-transparent border-white/5 hover:border-white/15 focus:border-amber-500/40 resize-y py-1 text-amber-300/80" data-testid={`matrix-notes-${si}-${ii}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
      {!sections.length && <p className="text-center text-sm text-muted-foreground py-6">Incarca documentele autoritatii/clientului si genereaza matricea: AI extrage fiecare cerinta si completeaza raspunsul ofertei SAGA. Poti edita apoi statusul si raspunsul direct in tabel.</p>}
    </div>
  );
}

export function GanttChartTab({ projectId, documents }) {
  const [charts, setCharts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [dragState, setDragState] = useState(null); // {actIdx, type:'move'|'resize', startX, origStart, origDuration}

  useEffect(() => { API.get(`/gantt-charts/${projectId}`).then(r => setCharts(r.data)).catch(() => {}); }, [projectId]);

  const generate = async () => {
    setGenerating(true);
    try { await API.post(`/generate/gantt/${projectId}`); toast.success('Grafic Gantt generat!');
      API.get(`/gantt-charts/${projectId}`).then(r => setCharts(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setGenerating(false); }
  };

  const latest = charts[0];
  const [localActivities, setLocalActivities] = useState([]);
  useEffect(() => { setLocalActivities(latest?.activities || []); }, [latest]);

  const totalMonths = latest?.total_months || 12;
  const months = Array.from({ length: totalMonths }, (_, i) => i + 1);
  const categories = [...new Set(localActivities.map(a => a.category))];
  const cellWidth = 48;

  const saveActivity = async (idx, data) => {
    if (!latest?.id) return;
    try { await API.put(`/gantt-charts/${latest.id}/activity/${idx}`, data); }
    catch { toast.error('Eroare salvare'); }
  };

  const handleMouseDown = (e, actIdx, type) => {
    e.preventDefault();
    const act = localActivities[actIdx];
    setDragState({ actIdx, type, startX: e.clientX, origStart: act.start_month, origDuration: act.duration });
  };

  useEffect(() => {
    if (!dragState) return;
    const handleMouseMove = (e) => {
      const dx = e.clientX - dragState.startX;
      const dMonths = Math.round(dx / cellWidth);
      setLocalActivities(prev => {
        const next = [...prev];
        const act = { ...next[dragState.actIdx] };
        if (dragState.type === 'move') {
          act.start_month = Math.max(1, Math.min(totalMonths, dragState.origStart + dMonths));
        } else if (dragState.type === 'resize-right') {
          act.duration = Math.max(1, dragState.origDuration + dMonths);
        } else if (dragState.type === 'resize-left') {
          const newStart = Math.max(1, dragState.origStart + dMonths);
          const diff = newStart - dragState.origStart;
          act.start_month = newStart;
          act.duration = Math.max(1, dragState.origDuration - diff);
        }
        next[dragState.actIdx] = act;
        return next;
      });
    };
    const handleMouseUp = () => {
      if (dragState) {
        const act = localActivities[dragState.actIdx];
        if (act) saveActivity(dragState.actIdx, { start_month: act.start_month, duration: act.duration });
      }
      setDragState(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, localActivities, totalMonths]);

  const exportXlsx = async () => {
    if (!latest?.id) return;
    try {
      const res = await API.get(`/export/gantt/${latest.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'gantt.xlsx'; a.click();
      toast.success('Gantt exportat!');
    } catch { toast.error('Eroare export'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <BarChart3 className="w-4 h-4 text-amber-400" />Grafic Gantt - Plan Implementare
          </h3>
          {localActivities.length > 0 && <p className="text-[10px] text-muted-foreground mt-1">Trage barele pentru a muta | Trage marginile pentru a redimensiona</p>}
        </div>
        <div className="flex gap-2">
          {latest && <Button onClick={exportXlsx} size="sm" variant="outline" className="text-xs" data-testid="export-gantt-xlsx"><Download className="w-3 h-3 mr-1" />Excel</Button>}
          <Button onClick={generate} disabled={generating || !documents.length} size="sm" data-testid="generate-gantt-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5 mr-1.5" />}
            {generating ? 'Se genereaza...' : 'Genereaza Gantt'}
          </Button>
        </div>
      </div>

      {localActivities.length > 0 ? (
        <Card className="bg-card border-white/10 overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto select-none">
              <div style={{ minWidth: `${240 + totalMonths * cellWidth}px` }}>
                {/* Header */}
                <div className="flex border-b border-white/10 bg-zinc-900/50 sticky top-0 z-10">
                  <div className="w-60 shrink-0 p-3 text-[10px] text-muted-foreground uppercase font-semibold border-r border-white/5">Activitate</div>
                  {months.map(m => (
                    <div key={m} style={{ width: cellWidth }} className="shrink-0 p-2 text-center text-[9px] text-muted-foreground border-r border-white/5">L{m}</div>
                  ))}
                </div>

                {/* Rows */}
                {localActivities.map((act, ai) => {
                  const catIdx = categories.indexOf(act.category);
                  const color = GANTT_COLORS[catIdx % GANTT_COLORS.length];
                  const barLeft = (act.start_month - 1) * cellWidth;
                  const barWidth = act.duration * cellWidth;

                  return (
                    <div key={ai} className="flex border-b border-white/5 hover:bg-white/[0.02]" data-testid={`gantt-row-${ai}`}>
                      <div className="w-60 shrink-0 p-2 border-r border-white/5">
                        <p className="text-xs font-medium truncate">{act.name}</p>
                        <p className="text-[9px] text-muted-foreground">{act.category} | L{act.start_month}-L{act.start_month + act.duration - 1}</p>
                      </div>
                      <div className="flex-1 relative" style={{ height: 36 }}>
                        {/* Grid lines */}
                        {months.map(m => (
                          <div key={m} className="absolute top-0 bottom-0 border-r border-white/5" style={{ left: m * cellWidth, width: 0 }} />
                        ))}
                        {/* Draggable bar */}
                        <div
                          className="absolute top-1.5 rounded cursor-grab active:cursor-grabbing group"
                          style={{ left: barLeft + 2, width: Math.max(barWidth - 4, 12), height: 22, backgroundColor: color, opacity: dragState?.actIdx === ai ? 0.9 : 0.7 }}
                          onMouseDown={e => handleMouseDown(e, ai, 'move')}
                        >
                          {/* Left resize handle */}
                          <div className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-white/30 rounded-l transition-opacity"
                            onMouseDown={e => { e.stopPropagation(); handleMouseDown(e, ai, 'resize-left'); }} />
                          {/* Right resize handle */}
                          <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize opacity-0 group-hover:opacity-100 hover:bg-white/30 rounded-r transition-opacity"
                            onMouseDown={e => { e.stopPropagation(); handleMouseDown(e, ai, 'resize-right'); }} />
                          {/* Label */}
                          {barWidth > 60 && <span className="absolute inset-0 flex items-center justify-center text-[9px] text-white font-medium truncate px-2">{act.duration}L</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Legend */}
                <div className="flex items-center gap-4 p-3 bg-zinc-900/30">
                  {categories.map((cat, ci) => (
                    <div key={cat} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: GANTT_COLORS[ci % GANTT_COLORS.length], opacity: 0.7 }} />
                      <span className="text-[10px] text-muted-foreground">{cat}</span>
                    </div>
                  ))}
                  <span className="text-[10px] text-muted-foreground ml-auto">Total: {totalMonths} luni</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <p className="text-center text-sm text-muted-foreground py-6">Genereaza un grafic Gantt din documentele proiectului</p>
      )}
    </div>
  );
}
